/* eslint prettier/prettier: 0 */
import { useContext, useCallback, useEffect, useRef } from 'react'
import isEqual from 'lodash/isEqual'
/* if-not-on-site-start */
import { ActiveAccountContext, UserDetailsContext } from '../user/Context'
import { UserDetailsType } from '../user/types'
/* if-not-on-site-end */
import { sendTelemetryEvent } from './telemetry'
import { TelemetryPayloadType } from './types'
import InstrumentationRootContext from './RootContext'

const MSEC_WAIT_FOR_ACTIVE_ACCOUNT = 2000

// this hook is used to provide an additional data key-value pairs to be included on any tracking calls
// (including page view)
export function usePageInstrumentationPayload(
  pageInstrumentationPayload: TelemetryPayloadType
) {
  const { pagePayload, setPageInstrumentationPayload } = useContext(
    InstrumentationRootContext
  ) || {}
  if (!isEqual(pagePayload, pageInstrumentationPayload)) {
    setPageInstrumentationPayload(pageInstrumentationPayload)
  }
}

export type SendInstrumentationFuncType = (
  eventName: string,
  payload?: TelemetryPayloadType,
  event?: Event
) => void

// the preferred way to send a telemetry event directly is to use this hook, since it will automatically include
// active account and user details
export function useSendTelemetry(
  telemetryFunc?: string
): SendInstrumentationFuncType {
  const { appName, pagePayload } = useContext(InstrumentationRootContext) || {}
  /* if-not-on-site-start */
  const activeAccountId = useContext(ActiveAccountContext)?.activeAccount
    ?.accountId
  const userDetails = useContext(UserDetailsContext)
    ?.response as UserDetailsType
  /* if-not-on-site-end */

  return useCallback(
    (eventName: string, eventPayload?: TelemetryPayloadType) => {
      sendTelemetryEvent({
        /* if-not-on-site-start */
        activeAccountId,
        userDetails,
        /* if-not-on-site-end */
        appName,
        eventName,
        eventPayload,
        pagePayload,
        telemetryFunc
      })
    },
    [activeAccountId, appName, pagePayload, telemetryFunc, userDetails]
  )
}

// this hook is intended to be called from CommonPage to record entry into your app.  It ensures that telemetry will
// not be sent until the user details and active accountId can be determined (these may not be available early in app
// load) and it ensures it will only be sent once.
export function useAppEntryTelemetry(): void {
  const timeoutRef = useRef<NodeJS.Timeout | undefined>()
  const {
    ensureAppEntryTelemetry,
    sendPageViewTelemetry,
    setPageInstrumentationPayload,
    waitingForPageInstrumentationFromApp
  } = useContext(InstrumentationRootContext) || {}
  /* if-not-on-site-start */
  const activeAccountId = useContext(ActiveAccountContext)?.activeAccount
    ?.accountId
  const userDetails = useContext(UserDetailsContext)
    ?.response as UserDetailsType
  /* if-not-on-site-end */

  // reset the extra configuration on page initialize, we expect the app to resend for the current page if it
  // plans to
  useEffect(() => {
    if (setPageInstrumentationPayload) {
      setPageInstrumentationPayload(undefined)
    }
  }, [setPageInstrumentationPayload])

  // The idea here is that we want to wait to send page view and app entry telemetry until we determine the active account
  // and user information, which may take a bit after app startup.  Also, in some cases, an application may inform us that
  // it plans to provide extra telemetry for each page view, and we should wait for that to be provided if we are expecting
  // Anytime additional needed details become available, it should re-trigger this effect.  The effect will either send the
  // telemetry after 1 msec (if we have all the data we are waiting for), or we will wait a moment (2 seconds) before
  // sending the telemetry (just in case we receive the missing data before the timeout expires, that will clear the pending
  // timeout to send the telemetry with the data known at the previous time the effect was invoked.
  // some apps have pages which don't contain account ids (global), so if we don't ever receive an update with an
  // account id within the specified amount of waiting time we can assume that we are in such a scenario and go
  // ahead and send telemetry without the account id.
  useEffect(() => {
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current)
    }
    if (ensureAppEntryTelemetry || sendPageViewTelemetry) {
      if (
        !waitingForPageInstrumentationFromApp
        /* if-not-on-site-start */
        && userDetails
        /* if-not-on-site-end */
      ) {
        timeoutRef.current = setTimeout(
          () => {
            if (sendPageViewTelemetry) {
              sendPageViewTelemetry(
                /* if-not-on-site-start */
                activeAccountId,
                userDetails
                /* if-not-on-site-end */
              )
            }
            if (ensureAppEntryTelemetry) {
              ensureAppEntryTelemetry(
                /* if-not-on-site-start */
                activeAccountId,
                userDetails
                /* if-not-on-site-end */
              )
            }
          },
          activeAccountId ? 1 : MSEC_WAIT_FOR_ACTIVE_ACCOUNT
        )
      }
    }
    return () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current)
        timeoutRef.current = undefined
      }
    }
  }, [
    /* if-not-on-site-start */
    activeAccountId,
    userDetails,
    /* if-not-on-site-end */
    ensureAppEntryTelemetry,
    sendPageViewTelemetry,
    waitingForPageInstrumentationFromApp
  ])
}

export default useSendTelemetry
